import { useEffect, useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';

export default function Hero() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { isDark, toggleTheme } = useTheme();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navBg = scrolled
    ? isDark
      ? 'bg-[#0f1117]/95 backdrop-blur-md border-b border-white/10'
      : 'bg-white/95 backdrop-blur-md shadow-sm'
    : 'bg-transparent';

  const navLinkColor = scrolled
    ? isDark ? 'rgba(209,213,219,0.9)' : '#374151'
    : 'rgba(255,255,255,0.85)';

  const btnBg = scrolled
    ? isDark ? 'rgba(255,255,255,0.08)' : '#111827'
    : 'rgba(255,255,255,0.15)';

  const btnBorder = scrolled
    ? isDark ? '1px solid rgba(255,255,255,0.15)' : 'none'
    : '1px solid rgba(255,255,255,0.3)';

  return (
    <section className="relative flex flex-col">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navBg}`}>
        <div className="max-w-7xl mx-auto px-8 lg:px-12">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <a href="#" className="flex items-center">
              <img
                src="https://public.readdy.ai/ai/img_res/1ffdb13e-c23c-4f92-af81-610280b8a7d6.png"
                alt="Fortixa"
                className="object-contain"
                style={{
                  width: '90px',
                  height: '90px',
                  filter: scrolled && !isDark ? 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' : 'none',
                }}
              />
            </a>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              {[
                { label: 'Services', href: '#services' },
                { label: 'Industries', href: '#industries' },
                { label: 'How It Works', href: '#how-it-works' },
                { label: 'Contact', href: '#contact' },
              ].map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  className="text-sm font-medium transition-colors whitespace-nowrap"
                  style={{ fontFamily: 'Montserrat, sans-serif', color: navLinkColor }}
                >
                  {link.label}
                </a>
              ))}

              {/* Day / Night Toggle */}
              <button
                onClick={toggleTheme}
                className="relative w-14 h-7 rounded-full transition-colors duration-300 cursor-pointer flex items-center px-1 flex-shrink-0"
                style={{
                  background: isDark ? '#2563EB' : scrolled ? '#E5E7EB' : 'rgba(255,255,255,0.2)',
                  border: scrolled ? 'none' : '1px solid rgba(255,255,255,0.25)',
                }}
                aria-label="Toggle dark mode"
              >
                <span
                  className="w-5 h-5 rounded-full flex items-center justify-center transition-all duration-300 bg-white"
                  style={{ transform: isDark ? 'translateX(28px)' : 'translateX(0)' }}
                >
                  <i
                    className={`${isDark ? 'ri-moon-fill' : 'ri-sun-fill'} text-xs`}
                    style={{ color: isDark ? '#2563EB' : '#F59E0B' }}
                  ></i>
                </span>
              </button>

              <a
                href="#contact"
                className="px-5 py-2 text-sm font-semibold rounded-full transition-all whitespace-nowrap cursor-pointer"
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  background: scrolled ? (isDark ? '#2563EB' : '#111827') : 'rgba(255,255,255,0.15)',
                  color: '#ffffff',
                  backdropFilter: 'blur(8px)',
                  border: btnBorder,
                }}
              >
                Get Started
              </a>
            </div>

            {/* Mobile right side */}
            <div className="md:hidden flex items-center gap-3">
              {/* Mobile Toggle */}
              <button
                onClick={toggleTheme}
                className="w-12 h-6 rounded-full flex items-center px-0.5 transition-colors duration-300 cursor-pointer"
                style={{ background: isDark ? '#2563EB' : 'rgba(255,255,255,0.25)', border: '1px solid rgba(255,255,255,0.3)' }}
                aria-label="Toggle dark mode"
              >
                <span
                  className="w-5 h-5 rounded-full flex items-center justify-center bg-white transition-all duration-300"
                  style={{ transform: isDark ? 'translateX(24px)' : 'translateX(0)' }}
                >
                  <i
                    className={`${isDark ? 'ri-moon-fill' : 'ri-sun-fill'} text-xs`}
                    style={{ color: isDark ? '#2563EB' : '#F59E0B' }}
                  ></i>
                </span>
              </button>

              <button
                className="w-8 h-8 flex items-center justify-center cursor-pointer"
                onClick={() => setMenuOpen(!menuOpen)}
              >
                <i
                  className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}
                  style={{ color: scrolled ? (isDark ? '#F9FAFB' : '#111827') : '#ffffff' }}
                ></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div
            className="md:hidden border-t px-8 py-4 space-y-3"
            style={{
              background: isDark ? '#0f1117' : '#ffffff',
              borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#F3F4F6',
            }}
          >
            {['Services', 'Industries', 'How It Works', 'Contact'].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase().replace(/\s+/g, '-')}`}
                className="block text-sm font-medium py-2 cursor-pointer"
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  color: isDark ? '#D1D5DB' : '#374151',
                }}
                onClick={() => setMenuOpen(false)}
              >
                {item}
              </a>
            ))}
          </div>
        )}
      </nav>

      {/* Hero — Full-screen dark cinematic */}
      <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0a0a0a]">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/edited_b003826d92e3306dff20ff0d97d003a1_8dc6e8b2.jpg"
            alt="AI background"
            className="w-full h-full object-cover object-center opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-8 lg:px-12 text-center">
          <p
            className="text-xs font-semibold tracking-[0.25em] uppercase text-[#2563EB] mb-8"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Intelligent Automation for Modern Organizations
          </p>
          <h1
            className="text-5xl lg:text-7xl font-bold text-white leading-[1.08] tracking-tight mb-8"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            AI Systems That
            <br />
            <span className="text-[#2563EB]">Strengthen</span> Your Business
          </h1>
          <p
            className="text-lg lg:text-xl text-white/70 leading-relaxed max-w-3xl mx-auto mb-12"
            style={{ fontFamily: 'Montserrat, sans-serif', fontWeight: 400 }}
          >
            Fortixa helps organizations deploy intelligent automation that improves efficiency,
            accelerates response times, and scales operations without increasing overhead.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#contact"
              className="px-8 py-4 bg-[#2563EB] text-white text-sm font-semibold rounded-full hover:bg-[#1d4ed8] transition-all shadow-lg hover:shadow-[#2563EB]/30 hover:shadow-2xl cursor-pointer whitespace-nowrap"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Start Your AI Assessment
            </a>
            <a
              href="#how-it-works"
              className="px-8 py-4 bg-white/10 text-white text-sm font-semibold rounded-full hover:bg-white/20 transition-all border border-white/20 cursor-pointer whitespace-nowrap backdrop-blur-sm"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              See How It Works
            </a>
          </div>
        </div>

        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-50">
          <span className="text-white text-xs tracking-widest uppercase" style={{ fontFamily: 'Montserrat, sans-serif' }}>Scroll</span>
          <div className="w-px h-10 bg-white/40 animate-pulse"></div>
        </div>
      </div>

      {/* Trust Bar */}
      <div
        className="border-b py-6 px-8 lg:px-12"
        style={{
          background: isDark ? '#0f1117' : '#ffffff',
          borderColor: isDark ? 'rgba(255,255,255,0.08)' : '#F3F4F6',
        }}
      >
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <p
            className="text-sm font-semibold tracking-wide"
            style={{ fontFamily: 'Montserrat, sans-serif', color: isDark ? '#D1D5DB' : '#374151' }}
          >
            Built for modern organizations that demand efficiency and operational clarity.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            {['Professional Services', 'Financial Services', 'Home Services', 'Healthcare', 'Real Estate'].map((industry) => (
              <span
                key={industry}
                className="px-4 py-1.5 text-xs font-medium rounded-full border"
                style={{
                  fontFamily: 'Montserrat, sans-serif',
                  background: isDark ? 'rgba(255,255,255,0.06)' : '#F3F4F6',
                  color: isDark ? '#9CA3AF' : '#374151',
                  borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#E5E7EB',
                }}
              >
                {industry}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
